import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AssignmentToolbar from '../components/assignments/AssignmentToolbar';
import AssignmentTable from '../components/assignments/AssignmentTable';
import AssignmentFormModal from '../components/assignments/AssignmentFormModal';
import AssignmentViewModal from '../components/assignments/AssignmentViewModal';
import {
  archiveAssignment,
  createAssignment,
  subscribeAssignments,
  subscribeClients,
  subscribeUsers,
  updateAssignment,
  updateOwnAssignmentProgress,
  linkAssignmentToMatter,
} from '../services/assignmentService';
import { subscribeAssignmentTypes } from '../services/assignmentTypeService';
import { createMatter, linkMatterToAssignment, recordAssignmentStatusChangeInMatter, subscribeMatters } from '../services/matterService';
import { assignmentTypeOf, displayName, dueState, financialYearOf, normalizedStatus } from '../utils/assignmentUtils';

export default function Assignments() {
  const { userData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefillMatter, setPrefillMatter] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignmentTypes, setAssignmentTypes] = useState([]);
  const [matters, setMatters] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dueFilter, setDueFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [error, setError] = useState('');

  const role = String(userData?.role || '').toLowerCase();
  const canManage = role === 'admin' || role === 'partner';

  useEffect(() => {
    if (!userData?.id) return undefined;
    const unsubscribeAssignments = subscribeAssignments({
      role,
      userId: userData.id,
      onData: setAssignments,
      onError: (err) => setError(`Could not load assignments: ${err.message}`),
    });
    const unsubscribeClients = subscribeClients(setClients, (err) => setError(`Could not load clients: ${err.message}`));
    const unsubscribeUsers = subscribeUsers(setUsers, (err) => setError(`Could not load staff: ${err.message}`));
    const unsubscribeAssignmentTypes = subscribeAssignmentTypes(setAssignmentTypes, (err) => setError(`Could not load assignment types: ${err.message}`));
    const unsubscribeMatters = subscribeMatters({ role, userId: userData.id, onData: setMatters, onError: (err) => setError(`Could not load matters: ${err.message}`) });
    return () => {
      unsubscribeAssignments();
      unsubscribeClients();
      unsubscribeUsers();
      unsubscribeAssignmentTypes();
      unsubscribeMatters();
    };
  }, [userData?.id, role]);


  useEffect(() => {
    const incoming = location.state?.createForMatter;
    if (!incoming || !canManage) return;
    setEditing(null);
    setPrefillMatter(incoming);
    setFormOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, canManage]);


  useEffect(() => {
    const targetId = searchParams.get('assignmentId');
    if (!targetId || !assignments.length) return;
    const target = assignments.find((item) => item.id === targetId);
    if (target) {
      setViewing(target);
      setSearchParams({}, { replace: true });
    }
  }, [assignments, searchParams, setSearchParams]);

  useEffect(() => {
    if (!location.state?.createAssignment || !canManage) return;
    setEditing(null);
    setPrefillMatter(null);
    setFormOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, canManage]);

  const clientName = (assignment) => assignment.clientName || clients.find((client) => client.id === assignment.clientId)?.name || assignment.clientId || '—';
  const staffName = (assignment) => assignment.assignedToName || displayName(users.find((person) => person.id === assignment.assignedToId)) || assignment.assignedTo || '—';

  const filteredAssignments = useMemo(() => {
    const term = search.trim().toLowerCase();
    return assignments.filter((assignment) => {
      const urlClientId = searchParams.get('clientId');
      if (urlClientId && String(assignment.clientId || '') !== String(urlClientId)) return false;
      if (!showArchived && assignment.isArchived === true) return false;
      if (showArchived && assignment.isArchived !== true) return false;
      if (statusFilter !== 'all' && normalizedStatus(assignment) !== statusFilter) return false;
      if (dueFilter !== 'all' && dueState(assignment) !== dueFilter) return false;
      if (!term) return true;
      const haystack = [
        clientName(assignment),
        staffName(assignment),
        assignmentTypeOf(assignment),
        financialYearOf(assignment),
        assignment.period,
        assignment.remarks,
        normalizedStatus(assignment),
      ].join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [assignments, clients, users, search, statusFilter, dueFilter, showArchived, searchParams]);

  const canEdit = (assignment) => canManage || (role === 'staff' && assignment.assignedToId === userData?.id);

  const openAdd = () => {
    setEditing(null);
    setPrefillMatter(null);
    setFormOpen(true);
  };

  const openEdit = (assignment) => {
    setEditing(assignment);
    setPrefillMatter(null);
    setFormOpen(true);
  };

  const save = async (values) => {
    try {
      setError('');
      let assignmentRef;
      if (!editing) {
        assignmentRef = await createAssignment(values, clients, users, userData);
      } else if (canManage) {
        await updateAssignment(editing.id, values, clients, users);
        assignmentRef = { id: editing.id };
      } else {
        await updateOwnAssignmentProgress(editing.id, values);
        assignmentRef = { id: editing.id };
      }

      if (canManage && (values.requiresMatter || prefillMatter?.matterId) && !editing?.matterId) {
        const assignmentRecord = {
          id: assignmentRef.id,
          ...values,
          clientName: clients.find((item) => item.id === values.clientId)?.name || '',
          assignedToName: displayName(users.find((item) => item.id === values.assignedToId)),
        };
        if (values.matterAction === 'link' && values.matterId) {
          const selectedMatter = matters.find((item) => item.id === values.matterId);
          await linkAssignmentToMatter(assignmentRef.id, values.matterId, selectedMatter?.matterCode || '');
          await linkMatterToAssignment(values.matterId, assignmentRecord, userData);
        } else {
          const matterRef = await createMatter({
            clientId: values.clientId,
            assignmentId: assignmentRef.id,
            assignmentType: values.assignmentType,
            financialYear: values.financialYear,
            period: values.period,
            assignedToId: values.assignedToId,
            title: values.matterTitle,
            lawType: values.matterLawType,
            currentStage: values.matterCurrentStage,
            noticeNumber: values.noticeNumber,
            authority: values.authority,
            noticeReceivedDate: values.noticeReceivedDate,
            replyDueDate: values.replyDueDate,
            nextHearingDate: values.nextHearingDate,
            appealDueDate: values.appealDueDate,
            status: 'Open',
            remarks: values.remarks,
          }, clients, users, userData);
          await linkAssignmentToMatter(assignmentRef.id, matterRef.id, matterRef.matterCode);
        }
      }

      if (editing?.matterId && editing.status !== values.status) {
        await recordAssignmentStatusChangeInMatter(
          editing.matterId,
          { id: editing.id, ...editing, ...values },
          editing.status,
          values.status,
          userData
        );
      }

      setFormOpen(false);
      setEditing(null);
      setPrefillMatter(null);
    } catch (err) {
      setError(`Could not save assignment: ${err.message}`);
    }
  };

  const archive = async (assignment) => {
    if (!window.confirm(`Archive assignment for ${clientName(assignment)}? Historical data will remain available.`)) return;
    try {
      await archiveAssignment(assignment.id);
    } catch (err) {
      setError(`Could not archive assignment: ${err.message}`);
    }
  };

  const counts = {
    total: assignments.filter((a) => !a.isArchived).length,
    today: assignments.filter((a) => !a.isArchived && dueState(a) === 'today').length,
    overdue: assignments.filter((a) => !a.isArchived && dueState(a) === 'overdue').length,
    completed: assignments.filter((a) => !a.isArchived && normalizedStatus(a) === 'Completed').length,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">Assignments</h1>
          <p className="text-sm text-gray-500">Manage client work, due dates, responsibility and progress.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded shadow p-3"><div className="text-xs text-gray-500">Open Register</div><div className="text-2xl font-bold">{counts.total}</div></div>
        <div className="bg-white rounded shadow p-3"><div className="text-xs text-gray-500">Due Today</div><div className="text-2xl font-bold">{counts.today}</div></div>
        <div className="bg-white rounded shadow p-3"><div className="text-xs text-gray-500">Overdue</div><div className="text-2xl font-bold text-red-600">{counts.overdue}</div></div>
        <div className="bg-white rounded shadow p-3"><div className="text-xs text-gray-500">Completed</div><div className="text-2xl font-bold text-green-600">{counts.completed}</div></div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-100 text-red-800 border border-red-200 rounded">{error}</div>}

      <AssignmentToolbar
        canCreate={canManage}
        search={search}
        onSearch={setSearch}
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
        dueFilter={dueFilter}
        onDueFilter={setDueFilter}
        showArchived={showArchived}
        onShowArchived={setShowArchived}
        onAdd={openAdd}
      />

      <AssignmentTable
        assignments={filteredAssignments}
        clientName={clientName}
        staffName={staffName}
        canManage={canManage}
        canEdit={canEdit}
        onView={setViewing}
        onEdit={openEdit}
        onArchive={archive}
      />

      <AssignmentFormModal
        open={formOpen}
        assignment={editing}
        prefillMatter={prefillMatter}
        clients={clients}
        users={users}
        assignmentTypes={assignmentTypes}
        matters={matters}
        canEditCore={canManage}
        onClose={() => { setFormOpen(false); setEditing(null); setPrefillMatter(null); }}
        onSave={save}
      />

      <AssignmentViewModal
        assignment={viewing}
        clientName={viewing ? clientName(viewing) : ''}
        staffName={viewing ? staffName(viewing) : ''}
        onClose={() => setViewing(null)}
      />
    </div>
  );
}
